/**
 * Christ AG Kazhakkoottam Home Page Component
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import churchLogo from '../../assets/cag-logo.png';
import churchLogoPlain from '../../assets/cag-logo-plain.png';
import churchLogoWood from '../../assets/cag-logo-wood.png';
import agLogo from '../../assets/ag-logo.png';
import pastorImage from '../../assets/pr-family.jpg';
import pastorSelvinImage from '../../assets/pastor-selvin.jpg';
import pastorGladvinImage from '../../assets/pastor-gladvin.jpg';

import churchGroupImage from '../../assets/church-group.jpg';
import gallery1 from '../../assets/gallery1.jpg';
import gallery2 from '../../assets/gallery2.jpg';
import gallery3 from '../../assets/gallery3.jpg';
import gallery4 from '../../assets/gallery4.jpg';
import './HomePage.css';
import { getAllEvents } from '../../services/eventService.firebase';
import { getAllArticles } from '../../services/articlesService.firebase';
import { getAllTestimonials } from '../../services/testimonialService.firebase';
import { getActiveAnnouncements } from '../../services/announcementService.firebase';
import { getAllGalleryPhotos } from '../../services/galleryService.firebase';
import { formatTo12Hour } from '../../utils/timeFormatter';
import YouTubeFeed from '../common/youtubeFeed';
import ChatWidget from '../chat/ChatWidget';
// Contact numbers for associate pastors. Update these values if you have specific numbers.
const tamilPastorPhone = '+917305839578';
const hindiPastorPhone = '+918590305446';

const sanitizeNumber = (num) => (num || '').toString().replace(/\D/g, '');
// Mock articles for demonstration
const MOCK_ARTICLES = [
  {
    id: 'mock-1',
    title: 'The Power of Daily Prayer',
    excerpt: 'Discover how establishing a daily prayer routine can transform your spiritual life and deepen your connection with God.',
    description: 'Prayer is the foundation of our faith. Learn practical tips for maintaining a meaningful prayer practice that brings you closer to God\'s love and purpose.',
    category: 'Prayer',
    author: 'Pastor Jobin Elisha',
    imageUrl: 'https://picsum.photos/400/250?random=1',
    link: '#',
    createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'mock-2',
    title: 'Finding Faith in Times of Uncertainty',
    excerpt: 'When life feels overwhelming, how can we maintain our faith and trust in God\'s plan? Explore biblical wisdom and practical guidance.',
    description: 'Faith is not about having all the answers. It\'s about trusting God even when we don\'t understand the journey. Discover how to strengthen your faith during challenging times.',
    category: 'Faith',
    author: 'Sister Maria',
    imageUrl: 'https://picsum.photos/400/250?random=2',
    link: '#',
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'mock-3',
    title: 'Living a Life of Gratitude',
    excerpt: 'Gratitude is a powerful spiritual practice that transforms our perspective. Learn how thanksgiving can deepen your faith journey.',
    description: 'A grateful heart opens doors to blessings we often overlook. Explore how to cultivate gratitude in your daily life and experience the peace that comes from appreciating God\'s goodness.',
    category: 'Faith',
    author: 'Brother James',
    imageUrl: 'https://picsum.photos/400/250?random=3',
    link: '#',
    createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
  },
];

const GALLERY_PHOTOS = [
  { id: 1, title: 'Youth Camp', url: gallery1 },
  { id: 2, title: 'Sunday Service', url: gallery2 },
  { id: 3, title: 'Sunday Schhol', url: gallery3 },
  { id: 4, title: 'Parenting sessions', url: gallery4 },
  { id: 5, title: 'Church Group', url: churchGroupImage },
];

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [weeklyEvents, setWeeklyEvents] = useState([]);
  const [articles, setArticles] = useState(MOCK_ARTICLES);
  const [testimonials, setTestimonials] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [isAnnouncementModalOpen, setIsAnnouncementModalOpen] = useState(false);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [allGalleryPhotos, setAllGalleryPhotos] = useState(GALLERY_PHOTOS);
  const [openSubmenu, setOpenSubmenu] = useState(null);

  // Gallery navigation handlers
  const handleNextPhoto = () => {
    if (!selectedPhoto) return;
    const currentIndex = allGalleryPhotos.findIndex(p => p.id === selectedPhoto.id);
    const nextIndex = (currentIndex + 1) % allGalleryPhotos.length;
    setSelectedPhoto(allGalleryPhotos[nextIndex]);
  };

  const handlePrevPhoto = () => {
    if (!selectedPhoto) return;
    const currentIndex = allGalleryPhotos.findIndex(p => p.id === selectedPhoto.id);
    const prevIndex = (currentIndex - 1 + allGalleryPhotos.length) % allGalleryPhotos.length;
    setSelectedPhoto(allGalleryPhotos[prevIndex]);
  };

  // Keyboard navigation for gallery
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isPhotoModalOpen) return;
      if (e.key === 'ArrowRight') handleNextPhoto();
      if (e.key === 'ArrowLeft') handlePrevPhoto();
      if (e.key === 'Escape') setIsPhotoModalOpen(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPhotoModalOpen, selectedPhoto]);

  // Scroll to resources section if coming from article detail page
  useEffect(() => {
    if (location.state?.scrollToResources) {
      // Use a longer timeout to ensure the DOM is fully rendered
      const scrollTimeout = setTimeout(() => {
        const resourcesContent = document.querySelector('.resources-content h2');
        if (resourcesContent) {
          window.scrollTo({
            top: resourcesContent.offsetTop - 100,
            behavior: 'smooth'
          });
        }
      }, 300);
      
      return () => clearTimeout(scrollTimeout);
    }
  }, [location]);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fade-in-visible');
        }
      });
    }, observerOptions);

    // Observe all sections and cards
    const elements = document.querySelectorAll('.about-card, .service-item, .ministry-card, .testimonial-card, .contact-item');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, [testimonials]); // Added testimonials dependency so observer updates when testimonials load

  // Load weekly events (Sunday to Saturday of current week) from Firebase
  useEffect(() => {
    let mounted = true;
    const loadWeekly = async () => {
      try {
        const allEvents = await getAllEvents();
        const now = new Date();
        
        // Calculate Sunday of the current week
        const day = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
        const sunday = new Date(now);
        sunday.setDate(now.getDate() - day);
        sunday.setHours(0, 0, 0, 0);
        
        // Calculate Saturday of the current week
        const saturday = new Date(sunday);
        saturday.setDate(sunday.getDate() + 6);
        saturday.setHours(23, 59, 59, 999);

        const weekEvents = allEvents
          .filter(e => {
            const d = new Date(e.date);
            return d >= sunday && d <= saturday;
          })
          .sort((a, b) => new Date(a.date) - new Date(b.date));

        if (mounted) setWeeklyEvents(weekEvents);
      } catch (err) {
        console.error('Failed to load weekly events', err);
      }
    };

    loadWeekly();
    return () => { mounted = false; };
  }, []);

  // Load articles from Firebase
  useEffect(() => {
    let mounted = true;
    const loadArticles = async () => {
      try {
        const allArticles = await getAllArticles();
        // Merge Firebase articles with mock articles (if any real articles exist, use them; otherwise use mock)
        const articlesToShow = allArticles.length > 0 ? allArticles : MOCK_ARTICLES;
        // Get the 5 most recent articles for homepage
        const recentArticles = articlesToShow.slice(0, 5);
        if (mounted) setArticles(recentArticles);
      } catch (err) {
        console.error('Failed to load articles', err);
        // Keep mock articles as fallback
      }
    };

    loadArticles();
    return () => { mounted = false; };
  }, []);

  // Load testimonials from Firebase
  useEffect(() => {
    let mounted = true;
    const loadTestimonials = async () => {
      try {
        console.log('Loading testimonials from Firebase...');
        const allTestimonials = await getAllTestimonials();
        console.log('Testimonials loaded from Firebase:', allTestimonials);
        
        if (mounted) {
          setTestimonials(allTestimonials);
        }
      } catch (err) {
        console.error('Failed to load testimonials from Firebase:', err);
        if (mounted) setTestimonials([]);
      }
    };

    loadTestimonials();
    return () => { mounted = false; };
  }, []);

  // Log testimonials state changes for debugging
  useEffect(() => {
    console.log('Testimonials state updated:', testimonials);
  }, [testimonials]);

  // Load active announcements from Firebase
  useEffect(() => {
    let mounted = true;
    const loadAnnouncements = async () => {
      try {
        const activeAnnouncements = await getActiveAnnouncements();
        if (mounted) {
          setAnnouncements(activeAnnouncements);
        }
      } catch (err) {
        console.error('Failed to load announcements:', err);
        if (mounted) setAnnouncements([]);
      }
    };

    loadAnnouncements();
    return () => { mounted = false; };
  }, []);

  // Load gallery photos from Firebase
  useEffect(() => {
    let mounted = true;
    const loadGalleryPhotos = async () => {
      try {
        const firebasePhotos = await getAllGalleryPhotos();
        // Transform Firebase photos to match the expected format
        const transformedPhotos = firebasePhotos.map(photo => ({
          id: photo.id,
          title: photo.title,
          url: photo.imageBase64,
          description: photo.description
        }));
        
        // Combine static photos with Firebase photos
        const combined = [...GALLERY_PHOTOS, ...transformedPhotos];
        
        if (mounted) {
          setGalleryPhotos(transformedPhotos);
          setAllGalleryPhotos(combined);
        }
      } catch (err) {
        console.error('Failed to load gallery photos:', err);
        if (mounted) {
          setAllGalleryPhotos(GALLERY_PHOTOS);
        }
      }
    };

    loadGalleryPhotos();
    return () => { mounted = false; };
  }, []);

  // Scroll to top button visibility and parallax effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollTopBtn = document.querySelector('.scroll-to-top');
      if (scrollTopBtn) {
        if (window.pageYOffset > 300) {
          scrollTopBtn.classList.add('visible');
        } else {
          scrollTopBtn.classList.remove('visible');
        }
      }

      // Parallax effect for hero
      const heroContent = document.querySelector('.hero-content');
      if (heroContent && window.pageYOffset < window.innerHeight) {
        const scrolled = window.pageYOffset;
        heroContent.style.transform = `translateY(${scrolled * 0.5}px)`;
        heroContent.style.opacity = 1 - (scrolled / window.innerHeight) * 0.8;
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="home-page">
      {/* Top Navigation Bar */}
      <nav className="home-navbar">
        <div className="navbar-container">
          <div className="navbar-logo">
            <img src={churchLogo} alt="CAG Logo" className="navbar-logo-img" />
            <span className="navbar-brand">Christ AG Church</span>
          </div>
          
          <button 
            className="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? '✕' : '☰'}
          </button>

          <div className={`navbar-menu ${isMobileMenuOpen ? 'active' : ''}`}>
            <button onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); setIsMobileMenuOpen(false); }} className="navbar-link">Home</button>
            <button onClick={() => { window.scrollTo({ top: document.querySelector('.about-section')?.offsetTop || 0, behavior: 'smooth' }); setIsMobileMenuOpen(false); }} className="navbar-link">About Us</button>
            <button onClick={() => { window.scrollTo({ top: document.querySelector('.services-section')?.offsetTop || 0, behavior: 'smooth' }); setIsMobileMenuOpen(false); }} className="navbar-link">Services</button>
            
            {/* Ministries Dropdown */}
            <div 
              className="navbar-dropdown"
              onMouseEnter={() => setOpenSubmenu('ministries')}
              onMouseLeave={() => setOpenSubmenu(null)}
            >
              <button 
                className="navbar-link navbar-dropdown-trigger"
                onClick={() => setOpenSubmenu(openSubmenu === 'ministries' ? null : 'ministries')}
              >
                Ministries <span className="dropdown-arrow">▼</span>
              </button>
              {openSubmenu === 'ministries' && (
                <div className="navbar-dropdown-menu">
                  <button onClick={() => { navigate('/ministry/childrens'); setIsMobileMenuOpen(false); setOpenSubmenu(null); }} className="navbar-dropdown-item">Children's Ministry</button>
                  <button onClick={() => { navigate('/ministry/youth'); setIsMobileMenuOpen(false); setOpenSubmenu(null); }} className="navbar-dropdown-item">Youth Ministry</button>
                  <button onClick={() => { navigate('/ministry/worship'); setIsMobileMenuOpen(false); setOpenSubmenu(null); }} className="navbar-dropdown-item">Worship Ministry</button>
                  <button onClick={() => { navigate('/ministry/family'); setIsMobileMenuOpen(false); setOpenSubmenu(null); }} className="navbar-dropdown-item">Family Ministry</button>
                  <button onClick={() => { navigate('/ministry/outreach'); setIsMobileMenuOpen(false); setOpenSubmenu(null); }} className="navbar-dropdown-item">Outreach Ministry</button>
                  <button onClick={() => { navigate('/ministry/prayer'); setIsMobileMenuOpen(false); setOpenSubmenu(null); }} className="navbar-dropdown-item">Prayer Ministry</button>
                  <button onClick={() => { navigate('/ministry/ladies'); setIsMobileMenuOpen(false); setOpenSubmenu(null); }} className="navbar-dropdown-item">Ladies Ministry</button>
                  <button onClick={() => { navigate('/ministry/mission'); setIsMobileMenuOpen(false); setOpenSubmenu(null); }} className="navbar-dropdown-item">Mission Ministry</button>
                </div>
              )}
            </div>

            <button onClick={() => { navigate('/celebrations'); setIsMobileMenuOpen(false); }} className="navbar-link">Celebrations</button>
            <button onClick={() => { navigate('/gallery'); setIsMobileMenuOpen(false); }} className="navbar-link">Gallery</button>
            <button onClick={() => { navigate('/articles'); setIsMobileMenuOpen(false); }} className="navbar-link">Articles</button>
            <button onClick={() => { navigate('/announcements'); setIsMobileMenuOpen(false); }} className="navbar-link">News</button>
            <button onClick={() => { navigate('/bible-reading-plan'); setIsMobileMenuOpen(false); }} className="navbar-link">Bible Reading</button>
            
            {/* Login Dropdown */}
            <div 
              className="navbar-dropdown"
              onMouseEnter={() => setOpenSubmenu('login')}
              onMouseLeave={() => setOpenSubmenu(null)}
            >
              <button 
                className="navbar-link navbar-dropdown-trigger"
                onClick={() => setOpenSubmenu(openSubmenu === 'login' ? null : 'login')}
              >
                Login <span className="dropdown-arrow">▼</span>
              </button>
              {openSubmenu === 'login' && (
                <div className="navbar-dropdown-menu">
                  <button onClick={() => { navigate('/login'); setIsMobileMenuOpen(false); setOpenSubmenu(null); }} className="navbar-dropdown-item">Admin Login</button>
                  <button onClick={() => { navigate('/member-portal/login'); setIsMobileMenuOpen(false); setOpenSubmenu(null); }} className="navbar-dropdown-item">Member Login</button>
                </div>
              )}
            </div>

            <button onClick={() => { navigate('/donate'); setIsMobileMenuOpen(false); }} className="navbar-link navbar-link-donate">Donate</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="church-header">
            <div className="hero-logos">
              <img src={churchLogo} alt="Christ AG Church Logo" className="hero-logo church-logo" />
            </div>
            <h1 className="church-name">Christ AG Church</h1>
            <p className="church-location">Kazhakkoottam, Thiruvananthapuram</p>
          </div>
          <p className="church-tagline">
            "For where two or three gather in my name, there am I with them." - Matthew 18:20
          </p>
          
          <div className="hero-cta">
           <button className="primary-cta" onClick={() => window.scrollTo({ top: document.querySelector('.services-section').offsetTop, behavior: 'smooth' })}>
              Explore Our Services
            </button>
            <button className="secondary-cta" onClick={() => window.scrollTo({ top: document.querySelector('.contact-section').offsetTop, behavior: 'smooth' })}>
              Get In Touch
            </button>
          </div>
        </div>
      </section>

      {/* Quick Info Banner */}
      <section className="info-banner">
        <div className="info-content">
          <div className="info-item">
            <div className="info-icon">👥</div>
            <div className="info-text">
              <h4>200+</h4>
              <p>Active Members</p>
            </div>
          </div>
          <div className="info-item">
            <div className="info-icon">📅</div>
            <div className="info-text">
              <h4>Weekly</h4>
              <p>Events & Services</p>
            </div>
          </div>
          <div className="info-item">
            <div className="info-icon">🎯</div>
            <div className="info-text">
              <h4>15+</h4>
              <p>Years of Ministry</p>
            </div>
          </div>
          <div className="info-item">
            <div className="info-icon">❤️</div>
            <div className="info-text">
              <h4>United</h4>
              <p>In Christ's Love</p>
            </div>
          </div>
        </div>
      </section>

      {/* Major Announcements Section */}
      {announcements.length > 0 && (
        <section className="announcements-section">
          <div className="announcements-content">
            <div className="announcements-header">
              <h2>📢 Latest News and Announcements</h2>
              <button 
                className="view-all-announcements-btn"
                onClick={() => navigate('/announcements')}
              >
                View All News →
              </button>
            </div>
            <div className="announcements-carousel">
              {announcements.map(announcement => (
                <div 
                  key={announcement.id} 
                  className="announcement-banner"
                  onClick={() => navigate(`/announcement/${announcement.id}`)}
                >
                  {announcement.imageBase64 && (
                    <div className="announcement-poster">
                      <img src={announcement.imageBase64} alt={announcement.title} />
                      <div className="announcement-overlay">
                        <div className="announcement-overlay-content">
                          <h3>{announcement.title}</h3>
                          <p>{announcement.description}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* About Section */}
      <section className="about-section">
        <div className="about-content">
          <h2>Welcome to Our Church Family</h2>
          
          {/* Pastor Introduction */}
          <div className="pastor-section">
            
            {/* Senior Pastor - Featured */}
            <div className="pastor-card senior-pastor">
              <div className="pastor-image-container">
                <img 
                  src={pastorImage} 
                  alt="Pastor Jobin Elisha" 
                  className="pastor-image"
                />
                <div className="pastor-icon-badge">✝️</div>
              </div>
              <div className="pastor-content">
                <h3>Pastor Jobin Elisha</h3>
                <p className="pastor-title">Senior Pastor</p>
                <p className="pastor-message">
                  "Welcome to Christ AG Church! We are a family united in faith, committed to worshiping God, 
                  growing together, and serving our community with Christ's love."
                </p>
              </div>
            </div>

            {/* Associate Pastors Grid */}
            <div className="associate-pastors-grid">
              {/* Associate Pastor - Tamil Service */}
              <div className="pastor-card associate-pastor">
                <div className="pastor-image-container associate-pastor-image">
                  <img 
                    src={pastorSelvinImage} 
                    alt="Pastor Selvin" 
                    className="pastor-image"
                  />
                  <div className="pastor-icon-badge">✝️</div>
                </div>
                <div className="pastor-content">
                  <h3>Pastor Selvin</h3>
                  <p className="pastor-title">Tamil Service Pastor</p>
                  <p className="pastor-message">
                    "Serving our Tamil-speaking congregation with dedication and love, bringing the message of Christ to our community."
                  </p>
                  <div className="pastor-ctas">
                    <a href={`tel:${tamilPastorPhone}`} className="cta-button call-cta">📞 {tamilPastorPhone}</a>
                    <a href={`https://wa.me/${sanitizeNumber(tamilPastorPhone)}`} target="_blank" rel="noopener noreferrer" className="cta-button whatsapp-cta">💬 WhatsApp</a>
                  </div>
                </div>
              </div>

              {/* Associate Pastor - Hindi Service */}
              <div className="pastor-card associate-pastor">
                <div className="pastor-image-container associate-pastor-image">
                  <img 
                    src={pastorGladvinImage} 
                    alt="Pastor Gladvin" 
                    className="pastor-image"
                  />
                  <div className="pastor-icon-badge">✝️</div>
                </div>
                <div className="pastor-content">
                  <h3>Pastor Gladvin</h3>
                  <p className="pastor-title">Hindi Service Pastor</p>
                  <p className="pastor-message">
                    "Ministering to our Hindi-speaking family with compassion, teaching God's Word and building His kingdom."
                  </p>
                  <div className="pastor-ctas">
                    <a href={`tel:${hindiPastorPhone}`} className="cta-button call-cta">📞 {hindiPastorPhone}</a>
                    <a href={`https://wa.me/${sanitizeNumber(hindiPastorPhone)}`} target="_blank" rel="noopener noreferrer" className="cta-button whatsapp-cta">💬 WhatsApp</a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="about-grid">
            <div className="about-card">
              <div className="about-icon">⛪</div>
              <h3>Our Mission</h3>
              <p>To spread the love of Christ and build a community of faith, hope, and love in Kazhakkoottam and beyond.</p>
            </div>
            <div className="about-card">
              <div className="about-icon">📖</div>
              <h3>Our Vision</h3>
              <p>Empowering believers to live purpose-driven lives through worship, fellowship, and service to God and community.</p>
            </div>
            <div className="about-card">
              <div className="about-icon">❤️</div>
              <h3>Our Values</h3>
              <p>Faith, Unity, Service, and Love guide everything we do as we walk together in Christ's footsteps.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section">
        <div className="services-content">
          <h2>Weekly Services & Events</h2>
          <div className="services-grid">
            {/* Sunday Services */}
            <div className="service-item">
              <div className="service-day">Sunday</div>
              <div className="service-details">
                <h4>🙏 English Service</h4>
                <p>7:00 AM - 8:45 AM</p>
              </div>
            </div>
            <div className="service-item">
              <div className="service-day">Sunday</div>
              <div className="service-details">
                <h4>📖 Sunday School & Bible Study</h4>
                <p>9:00 AM - 9:40 AM</p>
              </div>
            </div>
            <div className="service-item">
              <div className="service-day">Sunday</div>
              <div className="service-details">
                <h4>🙏 Malayalam Service</h4>
                <p>9:45 AM - 12:30 PM</p>
              </div>
            </div>
            <div className="service-item">
              <div className="service-day">Sunday</div>
              <div className="service-details">
                <h4>🙏 Hindi Service</h4>
                <p>4:00 PM - 5:30 PM</p>
              </div>
            </div>
            <div className="service-item">
              <div className="service-day">Sunday</div>
              <div className="service-details">
                <h4>🙏 Tamil Service</h4>
                <p>6:00 PM</p>
              </div>
            </div>
            
            {/* Weekday Services */}
            <div className="service-item">
              <div className="service-day">Tuesday</div>
              <div className="service-details">
                <h4>👥 Area Meeting</h4>
                <p>7:00 PM - 9:00 PM</p>
              </div>
            </div>
            <div className="service-item">
              <div className="service-day">Wednesday</div>
              <div className="service-details">
                <h4>👶 Prayer for the Children</h4>
                <p>10:00 AM</p>
              </div>
            </div>
            <div className="service-item">
              <div className="service-day">Thursday</div>
              <div className="service-details">
                <h4>✨ Faith Alive English Service</h4>
                <p>Gathering</p>
              </div>
            </div>
            <div className="service-item">
              <div className="service-day">Friday</div>
              <div className="service-details">
                <h4>🙏 Fasting Prayer</h4>
                <p>10:00 AM - 1:00 PM</p>
              </div>
            </div>
            <div className="service-item">
              <div className="service-day">Saturday</div>
              <div className="service-details">
                <h4>🎵 Worship Night</h4>
                <p>6:45 PM - 8:30 PM</p>
              </div>
            </div>
          </div>
          {weeklyEvents.length > 0 && (
            <div className="this-week-section">
              <div className="week-header">
                <h3>📅 This Week's Events</h3>
                <div className="week-duration">
                  {(() => {
                    const now = new Date();
                    const day = now.getDay();
                    const sunday = new Date(now);
                    sunday.setDate(now.getDate() - day);
                    
                    const saturday = new Date(sunday);
                    saturday.setDate(sunday.getDate() + 6);
                    
                    const sundayStr = sunday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    const saturdayStr = saturday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    
                    return <p>{sundayStr} - {saturdayStr}</p>;
                  })()}
                </div>
              </div>
              <div className="week-events-list">
                {weeklyEvents.map(e => {
                  const eventDate = new Date(e.date);
                  const today = new Date();
                  const isToday = today.toDateString() === eventDate.toDateString();
                  const dayName = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][eventDate.getDay()];
                  
                  return (
                    <div 
                      key={e.id} 
                      className={`home-week-event-card ${isToday ? 'today' : ''}`}
                      onClick={() => {
                        setSelectedEvent(e);
                        setIsModalOpen(true);
                      }}
                    >
                      <div className="event-date-badge">
                        <span className="day">{eventDate.getDate()}</span>
                        <span className="month">{eventDate.toLocaleDateString('en-US', { month: 'short' })}</span>
                        <span className="weekday">{dayName}</span>
                      </div>
                      <div className="event-details">
                        <h4 className="event-title">{e.title}</h4>
                        {e.description && <p className="event-description">{e.description}</p>}
                        <div className="event-meta-info">
                          {e.time && (
                            <span className="meta-item">
                              <span className="icon">🕐</span>
                              {formatTo12Hour(e.time)}
                            </span>
                          )}
                          {e.location && (
                            <span className="meta-item">
                              <span className="icon">📍</span>
                              {e.location}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* YouTube Feed Section */}
      <section className="youtube-section">
        <div className="youtube-content">
          <YouTubeFeed 
            channelId={process.env.REACT_APP_YOUTUBE_CHANNEL_ID} 
            apiKey={process.env.REACT_APP_YOUTUBE_API_KEY} 
          />
        </div>
      </section>

      {/* Ministries Section */}
      <section className="ministries-section">
        <div className="ministries-content">
          <h2>Our Ministries</h2>
          <div className="ministries-grid">
            <div className="ministry-card" onClick={() => navigate('/ministry/childrens')}>
              <div className="ministry-icon">👶</div>
              <h4>Children's Ministry</h4>
              <p>Nurturing young hearts with Bible stories, worship, and Christian values. Our Sunday School program provides age-appropriate biblical teaching, interactive lessons, and character-building activities for children of all ages.</p>
              <button className="ministry-btn">Learn More →</button>
            </div>
            <div className="ministry-card" onClick={() => navigate('/ministry/youth')}>
              <div className="ministry-icon">🎓</div>
              <h4>Youth Ministry</h4>
              <p>Empowering young people to grow in faith through Christ Ambassadors program. We provide mentorship, leadership development, biblical training, and fellowship opportunities that help youth discover their purpose in Christ.</p>
              <button className="ministry-btn">Learn More →</button>
            </div>
            <div className="ministry-card" onClick={() => navigate('/ministry/worship')}>
              <div className="ministry-icon">🎵</div>
              <h4>Worship Ministry</h4>
              <p>Leading the congregation in heartfelt praise and worship through music, song, and creative expression.</p>
              <button className="ministry-btn">Learn More →</button>
            </div>
            <div className="ministry-card" onClick={() => navigate('/ministry/family')}>
              <div className="ministry-icon">👨‍👩‍👧‍👦</div>
              <h4>Family Ministry</h4>
              <p>Strengthening families through special Bible study sessions and professional family counseling. We provide spiritual guidance, marriage enrichment programs, parenting support, and community fellowship activities.</p>
              <button className="ministry-btn">Learn More →</button>
            </div>
            <div className="ministry-card" onClick={() => navigate('/ministry/outreach')}>
              <div className="ministry-icon">🌍</div>
              <h4>Outreach Ministry</h4>
              <p>Sharing God's love through Gospel for Kazhakkuttom (GFK) initiative. Our outreach programs include community service, evangelism, humanitarian aid, and mission work to spread Christ's message beyond our walls.</p>
              <button className="ministry-btn">Learn More →</button>
            </div>
            <div className="ministry-card" onClick={() => navigate('/ministry/prayer')}>
              <div className="ministry-icon">🙏</div>
              <h4>Prayer Ministry</h4>
              <p>Interceding for our church, community, and nation through organized prayer chains and special prayer meetings on Zoom. Join us in powerful corporate prayer sessions that bring breakthroughs and blessings.</p>
              <button className="ministry-btn">Learn More →</button>
            </div>
            <div className="ministry-card" onClick={() => navigate('/ministry/ladies')}>
              <div className="ministry-icon">💐</div>
              <h4>Ladies Ministry (WMC)</h4>
              <p>Women's Missionary Council - Empowering women of faith through fellowship, Bible study, prayer meetings, and missionary support. Building a community of strong, godly women who impact their families and communities.</p>
              <button className="ministry-btn">Learn More →</button>
            </div>
            <div className="ministry-card" onClick={() => navigate('/ministry/mission')}>
              <div className="ministry-icon">✈️</div>
              <h4>Mission Department</h4>
              <p>Extending God's kingdom through strategic mission work in Telangana and beyond. Our mission teams establish churches, provide humanitarian aid, and spread the Gospel to unreached regions, transforming communities through Christ's love.</p>
              <button className="ministry-btn">Learn More →</button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="testimonials-content">
          <h2>Life Transformations</h2>
          {testimonials.length === 0 ? (
            <div className="no-testimonials">
              <p>No testimonials available at the moment. Check back soon!</p>
            </div>
          ) : (
            <div className="testimonials-grid">
              {testimonials.map(testimonial => (
                <div key={testimonial.id} className="testimonial-card">
                  <div className="testimonial-quote">"</div>
                  <p>{testimonial.content}</p>
                  <div className="testimonial-author">
                    <strong>{testimonial.name}</strong>
                    <span>{testimonial.role}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Gallery Section */}
      <section className="gallery-section">
        <div className="gallery-content">
          <h2>📸 Church Photo Gallery</h2>
          <p className="section-subtitle">Moments of worship, fellowship, and service in our community</p>
          <div className="gallery-grid">
            {allGalleryPhotos.slice(0, 5).map(photo => (
              <div 
                key={photo.id} 
                className="gallery-item"
                onClick={() => {
                  setSelectedPhoto(photo);
                  setIsPhotoModalOpen(true);
                }}
              >
                <img 
                  src={photo.url} 
                  alt={photo.title}
                  className="gallery-image"
                />
                <div className="gallery-overlay">
                  <div className="gallery-info">
                    <h4>{photo.title}</h4>
                    <p className="view-text">Click to view</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="gallery-view-all">
            <button 
              className="view-all-gallery-btn"
              onClick={() => navigate('/gallery')}
            >
              View All Photos →
            </button>
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section className="resources-section">
        <div className="resources-content">
          <h2>📚 Resources & Articles</h2>
          <p className="section-subtitle">Spiritual growth and learning materials for our community</p>
          
          {articles.length === 0 ? (
            <div className="no-articles">
              <p>No articles available at the moment. Check back soon!</p>
            </div>
          ) : (
            <>
              <div className="articles-grid">
                {articles.slice(0, 10).map(article => (
                  <div key={article.id} className="article-card">
                    {article.imageUrl && (
                      <div className="article-image">
                        <img 
                          src={article.imageUrl} 
                          alt={article.title}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    <div className="article-content">
                      <div className="article-meta">
                        <span className="article-category">{article.category || 'General'}</span>
                        <span className="article-date">
                          {new Date(article.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      </div>
                      <h3 className="article-title">{article.title}</h3>
                      <p className="article-excerpt">{article.excerpt || article.description}</p>
                      {article.author && (
                        <p className="article-author">By {article.author}</p>
                      )}
                      <button 
                        className="read-more-btn"
                        onClick={() => navigate(`/article/${article.id}`)}
                      >
                        Read More →
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="view-all-articles-section">
                <button 
                  className="btn-view-all-articles"
                  onClick={() => navigate('/articles')}
                >
                  View All Articles
                </button>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="quick-links-section">
        <div className="quick-links-content">
          <h2>Quick Access</h2>
          <div className="quick-links-grid">
            <div className="quick-link-card" onClick={() => navigate('/celebrations')}>
              <div className="quick-link-icon">🎉</div>
              <h4>Celebrations</h4>
              <p>View upcoming birthdays and anniversaries of our church family members.</p>
              <button className="quick-link-btn">View Celebrations →</button>
            </div>
            <div className="quick-link-card" onClick={() => navigate('/gallery')}>
              <div className="quick-link-icon">📸</div>
              <h4>Photo Gallery</h4>
              <p>Browse our collection of photos from worship services, ministries, and events.</p>
              <button className="quick-link-btn">View Gallery →</button>
            </div>
            <div className="quick-link-card" onClick={() => navigate('/articles')}>
              <div className="quick-link-icon">📖</div>
              <h4>All Articles</h4>
              <p>Explore our collection of inspiring articles, devotionals, and biblical teachings.</p>
              <button className="quick-link-btn">Read Articles →</button>
            </div>
            <div className="quick-link-card" onClick={() => navigate('/announcements')}>
              <div className="quick-link-icon">📰</div>
              <h4>All News</h4>
              <p>Stay updated with the latest news, announcements, and events from our church.</p>
              <button className="quick-link-btn">View News →</button>
            </div>
            <div className="quick-link-card" onClick={() => navigate('/donate')}>
              <div className="quick-link-icon">💝</div>
              <h4>Make a Donation</h4>
              <p>Support our ministry and church activities through your generous contribution.</p>
              <button className="quick-link-btn">Donate Now →</button>
            </div>
            <div className="quick-link-card" onClick={() => navigate('/bible-reading-plan')}>
              <div className="quick-link-icon">📕</div>
              <h4>Bible Reading Plan</h4>
              <p>Follow our one-year Bible reading plan and track your progress daily.</p>
              <button className="quick-link-btn">Start Reading →</button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section">
        <div className="contact-content">
          <h2>Visit Us or Get In Touch</h2>
          <div className="contact-grid">
            <div className="contact-item">
              <div className="contact-icon">📍</div>
              <h4>Address</h4>
              <p>2nd Floor, Mak Tower, 5705/4<br/>National Highway<br/>Near Kartika Park Hotel, Kazhakkoottam<br/>Thiruvananthapuram, Kerala 695582</p>
              <a href="https://maps.app.goo.gl/Wx4TuyxxA675yHzW7" target="_blank" rel="noopener noreferrer" className="map-link">
                🗺️ Open in Google Maps
              </a>
            </div>
            <div className="contact-item">
              <div className="contact-icon">📞</div>
              <h4>Phone</h4>
              <p>
                <a href="tel:+918590525909" className="phone-link">+91 85905 25909</a>
                <br/>
                <a href="https://wa.me/918590525909" target="_blank" rel="noopener noreferrer" className="whatsapp-link">
                  💬 WhatsApp Us
                </a>
                <br/>Office Hours:<br/>Mon-Sat: 9 AM - 5 PM
              </p>
            </div>
            <div className="contact-item">
              <div className="contact-icon">✉️</div>
              <h4>Email</h4>
              <p>waytoagkazhakkoottom@gmail.com</p>
            </div>
            <div className="contact-item">
              <div className="contact-icon">🌐</div>
              <h4>Connect Online</h4>
              <p><a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer">Facebook</a> | <a href="https://www.youtube.com/@assembliesofgodkazhakootta4792" target="_blank" rel="noopener noreferrer">YouTube</a> | <a href="https://www.instagram.com" target="_blank" rel="noopener noreferrer">Instagram</a><br/>Join our online community</p>
            </div>
          </div>
        </div>
      </section>

      {/* Portal Access Section */}
      <section className="portal-access-section">
        <div className="portal-access-content">
          <h3>Member & Staff Portal Access</h3>
          <p>Access your personal dashboard and church management tools</p>
          <div className="portal-links">
            <button className="portal-link-btn admin-link" onClick={() => navigate('/login')}>
              <span className="portal-link-icon">👨‍💼</span>
              <span className="portal-link-text">Admin Login</span>
            </button>
            <button className="portal-link-btn member-link" onClick={() => navigate('/member-portal/login')}>
              <span className="portal-link-icon">🙏</span>
              <span className="portal-link-text">Member Login</span>
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="home-footer">
        <div className="footer-content">
          <div className="footer-logo">
            <div className="footer-logos">
              <img src={churchLogoWood} alt="Christ AG Church" className="church-logo" />
            </div>
            <p>Christ AG Church, Kazhakkoottam</p>
          </div>
          <div className="footer-nav">
            <div className="footer-section">
              <h4>Quick Links</h4>
              <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="footer-link">🏠 Home</button>
              <button onClick={() => window.scrollTo({ top: document.querySelector('.about-section').offsetTop, behavior: 'smooth' })} className="footer-link">ℹ️ About Us</button>
              <button onClick={() => window.scrollTo({ top: document.querySelector('.services-section').offsetTop, behavior: 'smooth' })} className="footer-link">⛪ Services</button>
              <button onClick={() => window.scrollTo({ top: document.querySelector('.ministries-section').offsetTop, behavior: 'smooth' })} className="footer-link">🙏 Ministries</button>
              <button onClick={() => navigate('/celebrations')} className="footer-link">🎉 Celebrations</button>
              <button onClick={() => navigate('/weekly-events/slideshow')} className="footer-link">📅 This Week Events</button>
              <button onClick={() => navigate('/prayer-cards')} className="footer-link">🙏 Prayer Cards</button>
              <button onClick={() => navigate('/songs')} className="footer-link">🎵 Song library</button>

              <button onClick={() => navigate('/gallery')} className="footer-link">📸 Photo Gallery</button>
              <button onClick={() => navigate('/articles')} className="footer-link">📚 All Articles</button>
              <button onClick={() => navigate('/announcements')} className="footer-link">📢 All News</button>
              <button onClick={() => navigate('/donate')} className="footer-link">💝 Make a Donation</button>
              <button onClick={() => navigate('/sitemap')} className="footer-link">🗺️ Sitemap</button>
            </div>
            <div className="footer-section">
              <h4>Portal Access</h4>
              <button onClick={() => navigate('/login')} className="footer-link">🔐 Admin Login</button>
              <button onClick={() => navigate('/member-portal/login')} className="footer-link">👤 Member Login</button>
            </div>
            <div className="footer-section">
              <h4>Connect With Us</h4>
              <a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer" className="footer-link">📘 Facebook</a>
              <a href="https://www.instagram.com" target="_blank" rel="noopener noreferrer" className="footer-link">📷 Instagram</a>
              <a href="https://www.youtube.com/@assembliesofgodkazhakootta4792" target="_blank" rel="noopener noreferrer" className="footer-link">▶️ YouTube</a>
              <a href="https://www.twitter.com" target="_blank" rel="noopener noreferrer" className="footer-link">🐦 Twitter</a>
            </div>
          </div>
          <p className="footer-copyright">
            © 2025 Christ AG Church Kazhakkoottam. All rights reserved. | Built with ❤️ for God's Glory
          </p>
          </div>
      </footer>

      {/* Scroll to Top Button */}
      <button className="scroll-to-top" onClick={scrollToTop} aria-label="Scroll to top">
        <span>↑</span>
      </button>

      {/* Event Details Modal */}
      {isModalOpen && selectedEvent && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={() => setIsModalOpen(false)}>✕</button>
            
            <div className="modal-header">
              <h2>{selectedEvent.title}</h2>
            </div>

            <div className="modal-body">
              <div className="modal-section">
                <h3>📅 Date & Time</h3>
                <p>{new Date(selectedEvent.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
                {selectedEvent.time && <p><strong>Time:</strong> {formatTo12Hour(selectedEvent.time)}</p>}
              </div>

              {selectedEvent.location && (
                <div className="modal-section">
                  <h3>📍 Location</h3>
                  <p>{selectedEvent.location}</p>
                </div>
              )}

              {selectedEvent.description && (
                <div className="modal-section">
                  <h3>📝 Description</h3>
                  <p>{selectedEvent.description}</p>
                </div>
              )}

              {selectedEvent.zoomLink && (
                <div className="modal-section">
                  <h3>🎥 Join Online</h3>
                  <p>Zoom Meeting Available</p>
                  <button 
                    className="zoom-button"
                    onClick={() => {
                      if (selectedEvent.zoomLink.startsWith('http')) {
                        window.open(selectedEvent.zoomLink, '_blank');
                      } else {
                        window.location.href = selectedEvent.zoomLink;
                      }
                    }}
                  >
                    🔗 Join Zoom
                  </button>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="modal-btn-close" onClick={() => setIsModalOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Photo Lightbox Modal */}
      {isPhotoModalOpen && selectedPhoto && (
        <div className="photo-modal-overlay" onClick={() => setIsPhotoModalOpen(false)}>
          <div className="photo-modal-content" onClick={e => e.stopPropagation()}>
            <button className="photo-modal-close-btn" onClick={() => setIsPhotoModalOpen(false)}>✕</button>
            
            {/* Previous Button */}
            <button 
              className="photo-modal_nav-btn photo-modal-prev-btn" 
              onClick={(e) => {
                e.stopPropagation();
                handlePrevPhoto();
              }}
              aria-label="Previous photo"
            >
              ‹
            </button>
            
            {/* Next Button */}
            <button 
              className="photo-modal_nav-btn photo-modal-next-btn" 
              onClick={(e) => {
                e.stopPropagation();
                handleNextPhoto();
              }}
              aria-label="Next photo"
            >
              ›
            </button>
            
            <div className="photo-modal-body">
              <img 
                src={selectedPhoto.url} 
                alt={selectedPhoto.title}
                className="photo-modal-image"
              />
              <div className="photo-modal-info">
                <h3>{selectedPhoto.title}</h3>
                <p className="photo-counter">
                  {GALLERY_PHOTOS.findIndex(p => p.id === selectedPhoto.id) + 1} / {GALLERY_PHOTOS.length}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Announcement Detail Modal */}
      {isAnnouncementModalOpen && selectedAnnouncement && (
        <div className="announcement-modal-overlay" onClick={() => setIsAnnouncementModalOpen(false)}>
          <div className="announcement-modal-content" onClick={e => e.stopPropagation()}>
            <button className="announcement-modal-close-btn" onClick={() => setIsAnnouncementModalOpen(false)}>✕</button>
            <div className="announcement-modal-body">
              {selectedAnnouncement.imageBase64 && (
                <div className="announcement-modal-image">
                  <img src={selectedAnnouncement.imageBase64} alt={selectedAnnouncement.title} />
                </div>
              )}
              <div className="announcement-modal-info">
                <h2>{selectedAnnouncement.title}</h2>
                <p className="announcement-modal-description">{selectedAnnouncement.description}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Chat Widget */}
      <ChatWidget />
    </div>
  );
};
export default HomePage;